import { business, images } from '../data/business';
import { serviceAreas } from '../data/serviceAreas';
import { paintingServices } from '../data/services';
import { faqs } from '../data/faqs';
import type { Lang } from '../types/content';

export type SchemaObject = Record<string, unknown>;

const businessId = `${business.url}/#business`;
const areaServed = serviceAreas.map((a) => ({ '@type': 'City', name: `${a.city}, Ohio` }));

export function buildPageSchema(lang: Lang): SchemaObject[] {
  return [
    {
      '@context': 'https://schema.org',
      '@type': ['LocalBusiness', 'HousePainter'],
      '@id': businessId,
      name: business.name,
      alternateName: business.fullName,
      founder: { '@type': 'Person', name: business.owner },
      image: images.hero,
      url: business.url,
      telephone: business.phoneSchema,
      email: business.email,
      priceRange: '$$',
      knowsLanguage: ['en', 'es'],
      address: {
        '@type': 'PostalAddress',
        streetAddress: business.street,
        addressLocality: business.city,
        addressRegion: business.region,
        postalCode: business.postalCode,
        addressCountry: 'US',
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
          opens: '09:00',
          closes: '19:00',
        },
      ],
      areaServed,
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: business.rating.toFixed(1),
        reviewCount: String(business.reviewCount),
        bestRating: '5',
      },
    },
    ...(['commercial', 'residential'] as const).map((market) => ({
      '@context': 'https://schema.org',
      '@type': 'Service',
      name:
        market === 'commercial'
          ? lang === 'es' ? 'Pintura Comercial' : 'Commercial Painting'
          : lang === 'es' ? 'Pintura Residencial' : 'Residential Painting',
      serviceType: market === 'commercial' ? 'Commercial Painting' : 'House Painting',
      provider: { '@id': businessId },
      areaServed,
      hasOfferCatalog: {
        '@type': 'OfferCatalog',
        name: market === 'commercial' ? 'Commercial painting services' : 'Residential painting services',
        itemListElement: paintingServices
          .filter((s) => s.market === market)
          .map((s) => ({
            '@type': 'Offer',
            itemOffered: { '@type': 'Service', name: s.title[lang], description: s.description[lang] },
          })),
      },
    })),
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: lang,
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question[lang],
        acceptedAnswer: { '@type': 'Answer', text: f.answer[lang] },
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: lang === 'es' ? 'Inicio' : 'Home', item: `${business.url}/` },
        {
          '@type': 'ListItem',
          position: 2,
          name: lang === 'es' ? 'Servicios de Pintura' : 'Painting Services',
          item: `${business.url}/#services`,
        },
      ],
    },
  ];
}
